import classNames from 'classnames';
import SearchContact from 'components/Common/SearchContact';
import { usePlanning } from 'hooks/UsePlanning';
import { ActivityContactRole, Contact, Role } from 'openapi';
import React, { ReactNode, useCallback, useMemo, useRef } from 'react';
import { Control, Controller, FieldValues, Path } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InputError } from 'ui/InputError';
import { Label } from 'ui/Label';
import { contactName } from 'utilities/Contact';
import { defaultContactForHorseRole } from 'utilities/Horse';
import AddRoleToContact from './AddRoleToContactPanel';
import AddContactToPlanningPanel from './AddContactToPlanningPanel';
import { GroupBy } from 'utilities/Planning';
import { useOrganization } from 'context/OrganizationContext';
import { CacheKey } from 'utilities/ApiRequests';

export interface RoleOptions {
  role: Role;
  contacts: Contact[];
}

export type ActivityContactRoleInputProps<T extends FieldValues> = {
  selectedActivityTypeUid?: string;
  selectedHorseUid?: string;
  control?: Control<T>;
  name: Path<T>;
  label?: string;
  error?: string;
  required?: boolean;
  compact?: boolean;
};

interface ScrollContainerProps {
  children: ReactNode;
}

// A scroll container for horizontal scrolling
// This is used for the radio inputs of the roles.
function ScrollContainer({ children }: ScrollContainerProps): JSX.Element {
  const ref = useRef<HTMLDivElement>(null);

  return (
    <div
      ref={ref}
      className='flex mt-1.5 select-none overscroll-y-contain whitespace-nowrap overflow-y-hidden overflow-x-scroll no-scrollbar snap-x after:absolute after:inset-y-0 after:right-0 after:w-24 after:bg-gradient-to-l after:from-white after:pointer-events-none gap-x-1 pr-16'
      // Allow scrolling with the mousewheel (vertically) and scroll horizontally.
      onWheel={event => {
        if (!event.deltaY || event.deltaX) {
          return;
        }
        event.preventDefault();
        event.stopPropagation();
        ref.current?.scrollBy({
          left: event.deltaY < 0 ? -15 : 15,
        });
      }}
    >
      {children}
    </div>
  );
}

// Put the selected contact on top in the sort order. This way they show up first in the select list.
// This method should be called on submit.
export function storeSortedContacts(lastUsedContactUid: string, roleUid: string, cacheKey: CacheKey): void {
  if (cacheKey.name !== 'sortedActivityContacts') {
    throw Error('Wrong cache key');
  }
  const key = `${cacheKey.userUid}/${cacheKey.organizationUid}/${cacheKey.name}/${roleUid}`;
  let sorted = (localStorage.getItem(key) ?? '').split(',');
  const foundIndex = sorted.findIndex(uid => uid === lastUsedContactUid);
  if (foundIndex !== -1) {
    sorted.splice(foundIndex, 1);
  }
  sorted = [lastUsedContactUid, ...sorted];
  localStorage.setItem(key, sorted.join(','));
}

// A component that is used within SaveActivityForm.
// When we edit or create a new Activity (not Note, not FacilityEvent, etc) this
// component is show. It lets the user select the roles for the selected activity
// type.
export function ActivityContactRoleInput<T extends FieldValues>({
  control,
  name,
  selectedActivityTypeUid,
  selectedHorseUid,
  error,
  compact,
}: ActivityContactRoleInputProps<T>): JSX.Element {
  const { activityTypes, roles, contacts, horses, groupBy } = usePlanning();
  const { t } = useTranslation();
  const { selectedOrganizationUid, generateCacheKey } = useOrganization();

  // Sort the contacts based on usage (which is stored in the local storage)
  const sortedContacts = useCallback(
    (contacts: Contact[], role: Role): Contact[] => {
      const result = contacts ?? [];
      if (!selectedOrganizationUid) {
        return result;
      }
      const id = generateCacheKey('sortedActivityContacts');
      const key = `${id.userUid}/${id.organizationUid}/${id.name}/${role.uid}`;
      const sorted: Contact[] = [];
      for (const uid of (localStorage.getItem(key) ?? '').split(',')) {
        const foundIndex = result.findIndex(item => item.uid === uid);
        if (foundIndex !== -1) {
          sorted.push(result[foundIndex]);
        }
      }
      return [...sorted, ...result.filter(item => sorted.indexOf(item) === -1)];
    },
    [generateCacheKey, selectedOrganizationUid],
  );

  const rolesForActivity = useMemo((): Role[] => {
    let result: Role[] = [];
    if (selectedActivityTypeUid && activityTypes && roles) {
      const activity = activityTypes.find(activity => activity.uid === selectedActivityTypeUid);
      result = roles.filter(role => !role.hidden && activity?.possible_roles?.includes(role.uid));
    }
    return result;
  }, [selectedActivityTypeUid, roles, activityTypes]);

  const horse = useMemo(() => {
    return horses?.find(horse => horse.uid === selectedHorseUid);
  }, [horses, selectedHorseUid]);

  /**
   * Transform the list of contacts into radio buttons options for the given role.
   */
  const compactContactListForRole = useCallback(
    (role: Role): Contact[] => {
      const defaultContactUid = horse ? defaultContactForHorseRole(horse, role) : undefined;
      const result = contacts?.filter(contact => contact.roles?.includes(role.uid) || contact.uid === defaultContactUid) ?? [];
      return sortedContacts(result, role);
    },
    [contacts, horse, sortedContacts],
  );

  const roleOptions = useMemo((): RoleOptions[] => {
    return rolesForActivity.map((role): RoleOptions => {
      return { role, contacts: compactContactListForRole(role) };
    });
  }, [rolesForActivity, compactContactListForRole]);

  const updateValue = useCallback((currentValue: ActivityContactRole[], role: Role, contact?: Contact): unknown => {
    const filtered: ActivityContactRole[] = currentValue.filter(val => val.role_uid !== role.uid);
    if (contact) {
      filtered.push({ role_uid: role.uid, contact_uid: contact.uid, primary: false });
    }
    return filtered;
  }, []);

  const isSelected = useCallback((currentValue?: ActivityContactRole[], role?: Role, contact?: Contact) => {
    return currentValue?.find(val => val.role_uid === role?.uid && val.contact_uid === contact?.uid) !== undefined;
  }, []);

  const selectedContact = useCallback(
    (currentValue?: ActivityContactRole[], role?: Role): Contact | undefined => {
      const item = currentValue?.find(val => val.role_uid === role?.uid);
      return contacts?.find(contact => contact.uid === item?.contact_uid);
    },
    [contacts],
  );

  return (
    <div>
      <Controller
        name={name}
        control={control}
        render={({ field }) => {
          if (compact) {
            return (
              <div className='space-y-4'>
                {roleOptions.map(roleOption => {
                  if (roleOption.contacts.length === 0) {
                    return <></>;
                  }
                  const contSelected = selectedContact(field.value, roleOption.role);
                  return (
                    <div key={roleOption.role.uid} className='space-y-2'>
                      <div className='relative'>
                        <Label>{t('select-role', 'Select {{role}}', { role: roleOption.role.name ?? '' })}</Label>
                        <ScrollContainer>
                          {roleOption.contacts.map(contact => {
                            return (
                              <button
                                key={contact.uid}
                                type='button'
                                className={classNames(
                                  'flex items-center rounded-lg py-2 font-medium border cursor-pointer px-1.5 text-sm',
                                  {
                                    'bg-blue-600 text-white': isSelected(field.value, roleOption.role, contact),
                                  },
                                )}
                                onClick={() => {
                                  if (isSelected(field.value, roleOption.role, contact)) {
                                    field.onChange(updateValue(field.value, roleOption.role));
                                  } else {
                                    field.onChange(updateValue(field.value, roleOption.role, contact));
                                  }
                                }}
                              >
                                {contactName(contact)}
                              </button>
                            );
                          })}
                        </ScrollContainer>
                      </div>
                      {contSelected && <AddRoleToContact roleUid={roleOption.role.uid} contactUid={contSelected.uid} />}
                      {contSelected && groupBy === GroupBy.Staff && <AddContactToPlanningPanel contactUid={contSelected.uid} />}
                    </div>
                  );
                })}
              </div>
            );
          } else {
            return (
              <div className='space-y-4'>
                {roleOptions.map(roleOption => {
                  const contSelected = selectedContact(field.value, roleOption.role);
                  return (
                    <div key={roleOption.role.uid} className='space-y-2'>
                      <Label>{roleOption.role.name}</Label>
                      <SearchContact
                        pickRole={roleOption.role}
                        className='w-full mt-2'
                        contacts={contacts ?? []}
                        selectedContact={contSelected}
                        onSearch={contact => {
                          field.onChange(updateValue(field.value, roleOption.role, contact));
                        }}
                        onClear={() => {
                          field.onChange(updateValue(field.value, roleOption.role));
                        }}
                        placeholderText={t('select-role', 'Select {{role}}', { role: roleOption.role.name ?? '' })}
                      />
                      {contSelected && <AddRoleToContact roleUid={roleOption.role.uid} contactUid={contSelected.uid} />}
                      {contSelected && groupBy === GroupBy.Staff && <AddContactToPlanningPanel contactUid={contSelected.uid} />}
                    </div>
                  );
                })}
              </div>
            );
          }
        }}
      />
      {error && <InputError>{error}</InputError>}
    </div>
  );
}
